import React from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ProcedureStepNumber} from 'app/procedures/components/ProcedureStepNumber';
import {gt} from 'app/utils/i18n';

import {AssignableItemStructure} from './AssignableItemStructure';
import {AssignFormAssignToListStructure} from './AssignableListStructure';
import {AssignableStepType} from './AssignableSteps';

import {applyChangeToAssignment} from '../utils';

import {LaneSerializedType} from 'sweet-types';

import type {AssigneesByType, SetAssignmentsType} from '../types';

export type AssignableLaneWithStepType = {
  lane: LaneSerializedType;
  stepsInLane: Array<AssignableStepType>;
};

interface Props {
  lanes: Array<AssignableLaneWithStepType>;
  assignments: {[id: string]: AssigneesByType};
  setAssignments: SetAssignmentsType;
}

export function AssignFormAssignToLanes({
  lanes,
  assignments,
  setAssignments,
}: Props) {
  return lanes.length > 0 ? (
    <AssignFormAssignToListStructure>
      {({assigning, setAssigning, ListHeading}) => (
        <>
          <ListHeading>{gt('Lanes')}</ListHeading>
          {lanes.map(({lane, stepsInLane}) => (
            <AssignFormAssignToLaneItem
              key={lane.hashid}
              lane={lane}
              stepsInLane={stepsInLane}
              assigning={assigning === lane.hashid}
              setAssigning={(assigning: boolean) =>
                setAssigning(assigning ? lane.hashid : null)
              }
              assignees={assignments[lane.hashid]}
              setAssignments={setAssignments}
            />
          ))}
        </>
      )}
    </AssignFormAssignToListStructure>
  ) : null;
}

function AssignFormAssignToLaneItem({
  lane,
  stepsInLane,
  assigning,
  assignees,
  setAssigning,
  setAssignments,
}: {
  lane: LaneSerializedType;
  stepsInLane: Array<AssignableStepType>;
  assigning: boolean;
  assignees?: AssigneesByType;
  setAssigning: (assigning: boolean) => unknown;
  setAssignments: SetAssignmentsType;
}): JSX.Element {
  return (
    <AssignableItemStructure
      icon={<i className="fas fa-road" />}
      title={lane.name}
      assigning={assigning}
      assignees={assignees}
      setAssigning={setAssigning}
      onChange={(meta, ctname) =>
        applyChangeToAssignment(
          meta,
          ctname,
          'lane',
          lane.hashid,
          setAssignments
        )
      }
      postTitle={
        <div className="small d-flex align-items-center mt-1 mb-2">
          <div className="mr-1">{gt('Steps')}</div>
          {stepsInLane.map(({n, type, title}) => (
            <LazyTooltip key={n} tooltip={title}>
              <div style={{lineHeight: 1}}>
                <ProcedureStepNumber n={n} type={type} />
              </div>
            </LazyTooltip>
          ))}
        </div>
      }
    />
  );
}
